import {
  getPaginationRowModel,
  getFilteredRowModel,
  PaginationState,
  RowSelectionState,
} from "@tanstack/react-table"
import { DEFAULT_PAGE_SIZE } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import { CertificateCategorySchema } from "@tfg/types/schemas/certificates"

import { Sorts } from "~/components/SortBuilder"
import { useCustomReactTable } from "~/components/hooks/useCustomReactTable"
import {
  CertificateByOrganizationDto,
  useFindCertificatesByOrganizationId,
} from "~/gen/api"
import { CertificatesTable } from "~/pages/Brand/Certificates/components/CertificatesTable"
import {
  CertificatesListView,
  CertificatesListViewSchema,
} from "~/pages/Brand/Certificates/constants"
import { useColumns } from "~/pages/Brand/Certificates/hooks/columns"
import { useSearchParamsFilters } from "~/pages/Brand/Certificates/hooks/useSearchParamsFilters"
import {
  CertificatesTableContext,
  defaultContext,
} from "~/pages/Brand/Certificates/provider/CertificatesTableContext"

export const CertificatesTableProvider = () => {
  const [selectedCertificateInfo, setSelectedCertificateInfo] =
    useState<CertificateByOrganizationDto | null>(null)

  const params = useParams()

  const view =
    CertificatesListViewSchema.safeParse(params.view).data ??
    CertificatesListView.ALL
  const category =
    CertificateCategorySchema.safeParse(view.toLocaleUpperCase()).data ??
    undefined

  const [filters, setFilters, unsetFilter] = useSearchParamsFilters()
  const [sorts, setSorts] = useState<Sorts>({})
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  })
  const [search, setSearch] = useState<string>("")
  const [isColumnManagerOpen, setIsColumnManagerOpen] = useState(false)
  const {
    data,
    isPending: isLoading,
    isError,
    error,
  } = useFindCertificatesByOrganizationId({
    filters: {
      ...filters,
      ...(category ? { category } : {}),
    },
    pagination,
    search,
    sorts,
  })

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})

  useEffect(() => {
    setPagination({
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    })
  }, [search, filters])

  useEffect(() => {
    if (selectedCertificateInfo?.id) {
      const certificate = data?.data.certificates.find(
        (certificate) => certificate.id === selectedCertificateInfo.id
      )
      if (certificate) {
        setSelectedCertificateInfo(certificate)
      }
    }
  }, [data, selectedCertificateInfo?.id])

  const { certificates, total } = data?.data ?? {
    certificates: [],
    total: 0,
  }
  const columns = useColumns()
  const table = useCustomReactTable({
    data: certificates ?? [],
    columns,
    initialColumnPinning: {
      left: ["type_category", "type_name", "name"],
    },
    initialState: {
      pagination,
    },
    state: {
      pagination,
      rowSelection,
    },
    manualPagination: true,
    onPaginationChange: setPagination,
    pageCount: Math.ceil(total / pagination.pageSize),
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.id,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })
  return (
    <CertificatesTableContext.Provider
      value={{
        ...defaultContext,
        view,
        filters,
        data: data?.data ?? {
          certificates: [],
          total: 0,
        },
        isError,
        error,
        isLoading,
        selectedCertificateInfo,
        setSelectedCertificateInfo,
        setFilters,
        unsetFilter,
        rowSelection,
        setRowSelection,
        sorts,
        setSorts,
        pagination,
        setPagination,
        search,
        setSearch,
        columns,
        table,
        isColumnManagerOpen,
        setIsColumnManagerOpen,
      }}
    >
      <CertificatesTable />
    </CertificatesTableContext.Provider>
  )
}
